import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  Row,
  Col,
  FormGroup,
  Label,
  Button,
  Alert,
  Spinner,
  Badge
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FaArrowUp, FaArrowDown, FaExchangeAlt, FaAdjust } from 'react-icons/fa';
import { lotActions } from '../../sagas/lotSlice';

const MouvementStockModal = ({ lot, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Validation schema
  const validationSchema = Yup.object({
    typeMouvement: Yup.string().required(t('mouvement.typeRequired')),
    quantite: Yup.number()
      .positive(t('mouvement.quantitePositive'))
      .required(t('mouvement.quantiteRequired'))
      .test('stock-suffisant', t('mouvement.stockInsuffisant'), function(value) {
        const { typeMouvement } = this.parent;
        if (typeMouvement === 'SORTIE' && value > lot.quantiteDisponible) {
          return false;
        }
        return true;
      }),
    motif: Yup.string().required(t('mouvement.motifRequired')),
    referenceDocument: Yup.string()
  });

  // Initial values
  const initialValues = {
    typeMouvement: 'SORTIE',
    quantite: 0,
    motif: '',
    referenceDocument: ''
  };

  // Options pour les types de mouvement
  const typeMouvementOptions = [
    { 
      value: 'ENTREE', 
      label: t('mouvement.entree'), 
      icon: FaArrowUp, 
      color: 'success',
      description: t('mouvement.entreeDescription')
    },
    { 
      value: 'SORTIE', 
      label: t('mouvement.sortie'), 
      icon: FaArrowDown, 
      color: 'danger',
      description: t('mouvement.sortieDescription')
    },
    { 
      value: 'TRANSFERT', 
      label: t('mouvement.transfert'), 
      icon: FaExchangeAlt, 
      color: 'info',
      description: t('mouvement.transfertDescription')
    },
    { 
      value: 'AJUSTEMENT', 
      label: t('mouvement.ajustement'), 
      icon: FaAdjust, 
      color: 'warning',
      description: t('mouvement.ajustementDescription')
    },
    { 
      value: 'INVENTAIRE', 
      label: t('mouvement.inventaire'), 
      icon: FaAdjust, 
      color: 'primary',
      description: t('mouvement.inventaireDescription')
    }
  ];

  // Handle form submission
  const handleSubmit = (values, { setSubmitting }) => {
    setLoading(true);
    
    const payload = {
      numeroLot: lot.numeroLot,
      data: {
        ...values,
        dateMouvement: new Date().toISOString()
      },
      onSuccess: () => {
        Swal.fire({
          icon: 'success',
          title: t('common.success'),
          text: t('mouvement.createSuccess'),
        });
        onClose();
      },
      onError: (error) => {
        Swal.fire({
          icon: 'error',
          title: t('common.error'),
          text: error.message || t('mouvement.createError'),
        });
        setSubmitting(false);
        setLoading(false);
      }
    };

    dispatch(lotActions.createMouvementRequest(payload));
  };

  const calculateNewQuantity = (typeMouvement, quantite) => {
    switch (typeMouvement) {
      case 'ENTREE':
        return lot.quantiteDisponible + quantite;
      case 'SORTIE':
        return lot.quantiteDisponible - quantite;
      case 'AJUSTEMENT':
        return quantite; // La quantité saisie devient la nouvelle quantité
      case 'INVENTAIRE':
        return quantite; // La quantité saisie devient la nouvelle quantité
      default:
        return lot.quantiteDisponible;
    }
  };

  return (
    <div className="mouvement-stock-modal">
      {/* Informations du lot */}
      <Alert color="info" className="mb-4">
        <Row>
          <Col md={6}>
            <strong>{t('lot.numeroLot')}:</strong> {lot.numeroLot}
            <br />
            <strong>{t('lot.article')}:</strong> {lot.article?.nom || lot.articleNom}
          </Col>
          <Col md={6}>
            <strong>{t('lot.quantiteDisponible')}:</strong> 
            <span className="h5 text-primary ms-2">
              {lot.quantiteDisponible} {lot.article?.unite || 'U'}
            </span>
          </Col>
        </Row>
      </Alert>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting, errors, touched }) => {
          const selectedType = typeMouvementOptions.find(opt => opt.value === values.typeMouvement);
          const newQuantity = calculateNewQuantity(values.typeMouvement, parseFloat(values.quantite) || 0);
          
          return (
            <Form>
              {/* Sélection du type de mouvement */}
              <FormGroup className="mb-4">
                <Label>{t('mouvement.typeMouvement')} *</Label>
                <Row>
                  {typeMouvementOptions.map((option) => {
                    const IconComponent = option.icon;
                    return (
                      <Col md={6} lg={4} key={option.value} className="mb-3">
                        <div 
                          className={`card h-100 cursor-pointer ${
                            values.typeMouvement === option.value ? 'border-primary bg-light' : ''
                          }`}
                          onClick={() => setFieldValue('typeMouvement', option.value)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="card-body text-center p-3">
                            <div className="form-check">
                              <Field
                                type="radio"
                                name="typeMouvement"
                                value={option.value}
                                className="form-check-input"
                                id={`type-${option.value}`}
                              />
                            </div>
                            <IconComponent 
                              size={24} 
                              className={`text-${option.color} mb-2`} 
                            />
                            <div className="fw-bold">{option.label}</div>
                            <small className="text-muted">{option.description}</small>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
                <ErrorMessage name="typeMouvement" component="div" className="text-danger small mt-1" />
              </FormGroup>

              {/* Quantité */}
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="quantite">
                      {values.typeMouvement === 'AJUSTEMENT' || values.typeMouvement === 'INVENTAIRE' 
                        ? t('mouvement.nouvelleQuantite')
                        : t('mouvement.quantite')
                      } *
                    </Label>
                    <div className="input-group">
                      <Field
                        name="quantite"
                        type="number"
                        step="0.001"
                        className={`form-control ${errors.quantite && touched.quantite ? 'is-invalid' : ''}`}
                        placeholder="0.000"
                      />
                      <span className="input-group-text">
                        {lot.article?.unite || 'U'}
                      </span>
                    </div>
                    <ErrorMessage name="quantite" component="div" className="text-danger small mt-1" />
                    
                    {/* Aide contextuelle */}
                    {values.typeMouvement === 'SORTIE' && (
                      <small className="text-muted">
                        {t('mouvement.quantiteMaximale')}: {lot.quantiteDisponible} {lot.article?.unite || 'U'}
                      </small>
                    )}
                    {(values.typeMouvement === 'AJUSTEMENT' || values.typeMouvement === 'INVENTAIRE') && (
                      <small className="text-muted">
                        {t('mouvement.quantiteActuelle')}: {lot.quantiteDisponible} {lot.article?.unite || 'U'}
                      </small>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6}>
                  {/* Aperçu de la nouvelle quantité */}
                  {values.quantite > 0 && (
                    <div className="mt-4">
                      <Alert 
                        color={
                          newQuantity < 0 ? 'danger' :
                          newQuantity === 0 ? 'warning' : 'success'
                        }
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{t('mouvement.nouvelleQuantite')}:</strong>
                          </div>
                          <div className="h5 mb-0">
                            {newQuantity.toFixed(3)} {lot.article?.unite || 'U'}
                          </div>
                        </div>
                        {newQuantity < 0 && (
                          <small className="text-danger">
                            {t('mouvement.quantiteNegative')}
                          </small>
                        )}
                      </Alert>
                    </div>
                  )}
                </Col>
              </Row>

              {/* Motif */}
              <FormGroup>
                <Label for="motif">{t('mouvement.motif')} *</Label>
                <Field
                  as="textarea"
                  name="motif"
                  rows="3"
                  className={`form-control ${errors.motif && touched.motif ? 'is-invalid' : ''}`}
                  placeholder={t('mouvement.motifPlaceholder')}
                />
                <ErrorMessage name="motif" component="div" className="text-danger small mt-1" />
              </FormGroup>

              {/* Référence document */}
              <FormGroup>
                <Label for="referenceDocument">{t('mouvement.referenceDocument')}</Label>
                <Field
                  name="referenceDocument"
                  type="text"
                  className="form-control"
                  placeholder={t('mouvement.referenceDocumentPlaceholder')}
                />
                <small className="text-muted">
                  {t('mouvement.referenceDocumentHelp')}
                </small>
              </FormGroup>

              {/* Résumé du mouvement */}
              {values.quantite > 0 && selectedType && (
                <Alert color="light" className="border">
                  <h6 className="mb-3">{t('mouvement.resumeMouvement')}</h6>
                  <Row>
                    <Col md={3}>
                      <div className="text-center">
                        <Badge color={selectedType.color} className="p-2 mb-2">
                          <selectedType.icon className="me-1" />
                          {selectedType.label}
                        </Badge>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="text-muted small">{t('mouvement.quantiteActuelle')}</div>
                        <div className="fw-bold">{lot.quantiteDisponible} {lot.article?.unite || 'U'}</div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="text-muted small">
                          {values.typeMouvement === 'AJUSTEMENT' || values.typeMouvement === 'INVENTAIRE' 
                            ? t('mouvement.nouvelleQuantite')
                            : t('mouvement.quantiteMouvement')
                          }
                        </div>
                        <div className={`fw-bold ${
                          values.typeMouvement === 'ENTREE' ? 'text-success' : 
                          values.typeMouvement === 'SORTIE' ? 'text-danger' : 'text-info'
                        }`}>
                          {values.typeMouvement === 'ENTREE' ? '+' : 
                           values.typeMouvement === 'SORTIE' ? '-' : ''}
                          {values.quantite} {lot.article?.unite || 'U'}
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="text-muted small">{t('mouvement.quantiteApres')}</div>
                        <div className={`fw-bold h5 mb-0 ${
                          newQuantity < 0 ? 'text-danger' :
                          newQuantity === 0 ? 'text-warning' : 'text-success'
                        }`}>
                          {newQuantity.toFixed(3)} {lot.article?.unite || 'U'}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Alert>
              )}

              {/* Boutons d'action */}
              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  type="button"
                  color="secondary"
                  onClick={onClose}
                  disabled={isSubmitting || loading}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting || loading || newQuantity < 0}
                >
                  {(isSubmitting || loading) && <Spinner size="sm" className="me-2" />}
                  {t('mouvement.enregistrer')}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default MouvementStockModal;
